import { ManageModal } from "@/components/ManageModal/ManageModal";
import { useManageModalConfig } from "@/components/ManageModal/manageConfig";
import { renderManagePane } from "@/components/ManageModal/managePanes";
import { ROUTES } from "@/constants/routes";
import { OpenManagePlanProvider } from "@/context/OpenManagePlanContext";
import {
  OpenSettingsProvider,
  type OpenSettingsSource,
} from "@/context/OpenSettingsContext";
import { PaymentMethodDialogProvider } from "@/context/PaymentMethodDialogContext";
import { SettingsInDialogProvider } from "@/context/SettingsInDialogContext";
import { SettingsInitialNavigationProvider } from "@/context/SettingsInitialNavigationContext";
import { useManagePlan } from "@/hooks/useManagePlan";
import { usePaymentMethodDialog } from "@/hooks/usePaymentMethodDialog";
import { trackSettingsOpened } from "@/utils/analytics";
import {
  isUsagePlanPath,
  normalizeOpenSettingsInput,
  resolveManageEntry,
  type OpenSettingsInput,
  type OpenSettingsState,
} from "@/utils/settingsNavigation";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSettingsRouteSync } from "./useSettingsRouteSync";

interface LayoutSettingsShellProps {
  children: React.ReactNode;
}

/**
 * Mounts the Manage modal once per layout and provides `openSettings`.
 *
 * `openSettings(path)` is the one opening contract — every entry point names a
 * path and the shell's alias/landing maps pick the tab and scope. The full
 * path also rides the initial-navigation context so a pane can drill in
 * (a connector detail link lands on the Connectors tab, opened on that
 * connector).
 */
export function LayoutSettingsShell({ children }: LayoutSettingsShellProps) {
  // One modal per layout: the rail's "Manage plan" action, the Plan pane's
  // button and the /settings/usage/plan deep link all open this one.
  const { openManagePlan, closeManagePlan, managePlan } = useManagePlan(
    ROUTES.SETTINGS.USAGE,
  );
  const {
    openPaymentMethodDialog,
    closePaymentMethodDialog,
    paymentMethodDialog,
  } = usePaymentMethodDialog();
  const config = useManageModalConfig({ onManagePlan: openManagePlan });
  const [entry, setEntry] = useState<string | null>(null);
  const [managePlanPending, setManagePlanPending] = useState(false);
  const [initialNavigation, setInitialNavigation] = useState<OpenSettingsState>(
    {},
  );

  const openSettings = useCallback(
    (input?: OpenSettingsInput, source: OpenSettingsSource = "ui") => {
      const normalized = normalizeOpenSettingsInput(input);
      setInitialNavigation(normalized);
      const resolved = normalized.pathname
        ? resolveManageEntry(normalized.pathname, config.allMenuItems)
        : undefined;
      const entryPath = resolved ?? config.defaultTab ?? null;
      setEntry(entryPath);
      // "/settings/<tab>/<scope>" — ids only, never content. One emit per
      // openSettings call; the manage-plan dialog it may arm below is part of
      // the same open, not a second one.
      const segments = (normalized.pathname ?? entryPath ?? "")
        .split("/")
        .filter(Boolean);
      trackSettingsOpened({ tab: segments[1], scope: segments[2], source });

      // The one settings path that also opens a dialog. Armed here rather than
      // in a pane, so it is spent with the openSettings call that carried it —
      // a rail-scope switch can't re-open it. `resolved` gates it: a member has
      // no Usage tab, so the shell falls back to the default tab and must not
      // also mount admin-only billing queries.
      //
      // A card deep link, if one is ever added, needs the identical pending+rAF
      // treatment and must be mutually exclusive with this one: two dialogs
      // opening in the same frame race for Radix's top layer.
      setManagePlanPending(!!resolved && isUsagePlanPath(normalized.pathname));
    },
    [config.allMenuItems, config.defaultTab],
  );

  // Radix stacks dialogs by the order their layers mount: the last one in owns
  // Escape and is the one `hideOthers` leaves visible to assistive tech. The
  // Manage modal's content mounts a commit behind `entry` (Presence flips on a
  // layout effect), so a modal opened in the same batch as `setEntry` mounts
  // first and lands *under* Settings — inert, aria-hidden, and Escape closes
  // Settings out from under it. A frame is what separates the two commits.
  useEffect(() => {
    if (!managePlanPending || entry === null) return;
    const frame = requestAnimationFrame(() => {
      setManagePlanPending(false);
      openManagePlan();
    });
    return () => cancelAnimationFrame(frame);
  }, [managePlanPending, entry, openManagePlan]);

  const closeSettings = useCallback(() => {
    setEntry(null);
    setManagePlanPending(false);
    setInitialNavigation({});
    // The route sync closes settings from outside the dialogs, so the shell owns
    // every lifetime — otherwise the manage-plan modal, or a card form,
    // outlives the settings dialog it opened over.
    closeManagePlan();
    closePaymentMethodDialog();
  }, [closeManagePlan, closePaymentMethodDialog]);

  useSettingsRouteSync(openSettings, entry !== null, closeSettings);

  // A pane consumes the deep-linked path once; switching tabs by hand must not
  // re-drill into it.
  const initialNavigationValue = useMemo(
    () => ({ pathname: initialNavigation.pathname }),
    [initialNavigation.pathname],
  );

  return (
    <OpenSettingsProvider value={openSettings}>
      <OpenManagePlanProvider value={openManagePlan}>
        {/* Wraps `children`, not just the modal subtree: `UsageLimitBanner`
            renders inside the shell but outside the Manage modal. */}
        <PaymentMethodDialogProvider value={openPaymentMethodDialog}>
          {children}
          <SettingsInDialogProvider value={true}>
            <SettingsInitialNavigationProvider value={initialNavigationValue}>
              <ManageModal
                // Every trigger in the product says "Settings" — the gear, the
                // account-menu row, the deep links. The design system's shell
                // defaults its accessible name to "Manage" (its component name)
                // and expects the consumer to name it; announcing "Manage dialog"
                // to a screen reader after clicking Settings is a mismatch, and it
                // was called Settings before this refactor.
                title="Settings"
                tabs={config.tabs}
                rails={config.rails}
                aliases={config.aliases}
                landing={config.landing}
                tab={entry}
                onTab={(tab) => {
                  setEntry(tab);
                  setInitialNavigation({});
                }}
                onClose={closeSettings}
                renderPane={renderManagePane}
              />
              {managePlan}
              {paymentMethodDialog}
            </SettingsInitialNavigationProvider>
          </SettingsInDialogProvider>
        </PaymentMethodDialogProvider>
      </OpenManagePlanProvider>
    </OpenSettingsProvider>
  );
}
